import React, { useState, useCallback, useEffect } from 'react';

import { Input, Tag } from 'antd';

import styled from 'styled-components';

import FlexContainer from '@components/Gui/FlexContainer';
import MiniLoading from '@components/Gui/MiniLoading';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import debounce from 'lodash/debounce';

const EventTypeTag = styled(Tag)`
    cursor: pointer;
    padding: 0.2rem 0.5rem;
    font-size: ${(props) => props.theme.FontSizeSm};
    text-transform: uppercase;
    border-radius: 4px;
    user-select: none;
    transition: all 0.2s;

    &.active {
        background-color: ${(props) => props.theme.PrimaryColor};
        color: #fff;
        border-color: ${(props) => props.theme.PrimaryColor};

        &:hover {
            background-color: ${(props) => props.theme.PrimaryColorDark || props.theme.PrimaryColor};
            border-color: ${(props) => props.theme.PrimaryColorDark || props.theme.PrimaryColor};
        }
    }

    &.inactive {
        background-color: ${(props) => props.theme.LightGrayColor || '#f5f5f5'};
        color: ${(props) => props.theme.TextMutedColor || '#8c8c8c'};
        border-color: ${(props) => props.theme.BorderColor || '#d9d9d9'};

        &:hover {
            background-color: ${(props) => props.theme.BorderColor || '#d9d9d9'};
        }
    }
`;

const StyledHeader = styled.div`
    margin: 0;
    padding: 1.5rem 1rem;
    position: relative;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: 0.9rem;
    margin-bottom: 2rem;

    .header-first-row {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;
        gap: 1rem;

        .header-summary {
            flex: 0 0 auto;

            .header-summary-text {
                font-size: 1em;
                display: flex;
                align-items: center;

                em.counter {
                    font-size: 1rem;
                    font-weight: bold;
                    font-style: normal;
                    padding-right: 0.5em;
                }
            }
        }

        .control {
            flex: 1 1 auto;
        }
    }

    .header-second-row {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 0.5rem;

        .add-models-as {
            margin-left: auto;
            display: flex;
            align-items: center;
            font-size: ${(props) => props.theme.FontSizeSm};
            text-transform: uppercase;
            white-space: nowrap;
        }
    }
`;

const AvailableEventsHeader = ({
    filteredCount,
    totalCount,
    loading,
    filters,
    onFilterChange,
    addModelsAs,
    onAddModelsAsChange,
}) => {
    const [localName, setLocalName] = useState(filters.nameFilter);

    useEffect(() => {
        setLocalName(filters.nameFilter);
    }, [filters.nameFilter]);

    const debouncedChange = useCallback(
        debounce((value) => {
            onFilterChange('nameFilter', value);
        }, 400),
        [onFilterChange]
    );

    useEffect(() => {
        debouncedChange(localName);
        return () => {
            debouncedChange.cancel();
        };
    }, [localName, debouncedChange]);

    const counterText =
        filteredCount === totalCount ? `${totalCount} EVENTS` : `${filteredCount} / ${totalCount} EVENTS`;

    return (
        <StyledHeader>
            <div className="header-first-row">
                <div className="header-summary">
                    {!loading ? (
                        <div className="header-summary-text">
                            <em className="counter">{counterText}</em>
                        </div>
                    ) : (
                        <FlexContainer alignItems="center" gap="0.5rem">
                            <MiniLoading />
                            <span>Fetching Events...</span>
                        </FlexContainer>
                    )}
                </div>
                <div className="control">
                    <Input
                        allowClear
                        autoComplete="off"
                        placeholder="Filter by name"
                        prefix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                        value={localName}
                        onChange={(e) => setLocalName(e.target.value)}
                    />
                </div>
            </div>
            <div className="header-second-row">
                <EventTypeTag
                    className={filters.includeCastings ? 'active' : 'inactive'}
                    onClick={() => onFilterChange('includeCastings', !filters.includeCastings)}
                >
                    Castings
                </EventTypeTag>
                <EventTypeTag
                    className={filters.includeJobs ? 'active' : 'inactive'}
                    onClick={() => onFilterChange('includeJobs', !filters.includeJobs)}
                >
                    Jobs
                </EventTypeTag>
                <EventTypeTag
                    className={filters.includeExtJobs ? 'active' : 'inactive'}
                    onClick={() => onFilterChange('includeExtJobs', !filters.includeExtJobs)}
                >
                    Ext Jobs
                </EventTypeTag>
                <div className="add-models-as">
                    add model as &nbsp;&nbsp;
                    <RadioButtonGroup
                        buttonStyle="solid"
                        value={addModelsAs}
                        options={[
                            { key: 0, value: 0, label: 'option' },
                            { key: 10, value: 10, label: 'confirmed' },
                        ]}
                        onChange={onAddModelsAsChange}
                    />
                </div>
            </div>
        </StyledHeader>
    );
};

export default AvailableEventsHeader;
